import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Loader2 } from 'lucide-react';
import { API } from './analyticsHelpers.js';
import './AnalyticsDashboard.css';

import OverviewTab  from './OverviewTab.js';
import HeatmapTab   from './HeatmapTab.js';
import MarkersTab   from './MarkersTab.js';
import ActivityTab  from './ActivityTab.js';
import SessionsTab  from './SessionsTab.js';
import ZoomTab      from './ZoomTab.js';

const TABS = [
    { id: 'overview',  label: 'Overview'  },
    { id: 'heatmap',   label: 'Heatmap'   },
    { id: 'markers',   label: 'Markers'   },
    { id: 'activity',  label: 'Activity'  },
    { id: 'sessions',  label: 'Sessions'  },
    { id: 'zoom',      label: 'Viewport'  },
];

const AnalyticsDashboard = () => {
    const { mapId } = useParams();
    const navigate  = useNavigate();

    const token = localStorage.getItem('popmap_token');
    const authHeader = useMemo(() => ({
        Authorization: `Bearer ${token}`,
    }), [token]);

    const [loading,       setLoading]       = useState(true);
    const [error,         setError]         = useState(null);
    const [analyticsData, setAnalyticsData] = useState(null);
    const [pings,         setPings]         = useState([]);
    const [clicks,        setClicks]        = useState([]);
    const [viewports,     setViewports]     = useState([]);
    const [refreshing,    setRefreshing]    = useState(false);
    const [activeTab,     setActiveTab]     = useState('overview');

    const fetchData = useCallback(async () => {
        try {
            const [analyticsRes, pingsRes, clicksRes] = await Promise.all([
                fetch(`${API}/api/analytics/map/${mapId}`,         { headers: authHeader }),
                fetch(`${API}/api/analytics/map/${mapId}/pings`,   { headers: authHeader }),
                fetch(`${API}/api/analytics/map/${mapId}/clicks`,  { headers: authHeader }),
            ]);

            const [analytics, pingsData, clicksData] = await Promise.all([
                analyticsRes.json(),
                pingsRes.json(),
                clicksRes.json(),
            ]);

            if (!analytics.success) throw new Error(analytics.message || 'Failed to load analytics');

            setAnalyticsData(analytics.data);
            setPings(pingsData.data?.pings   || []);
            setClicks(clicksData.data?.clicks || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [mapId, authHeader]);

    const fetchViewports = useCallback(async () => {
        try {
            const res = await fetch(
                `${API}/api/analytics/map/${mapId}/viewports`,
                { headers: authHeader }
            );
            const data = await res.json();
            if (data.success) setViewports(data.data.viewports || []);
        } catch (_) {
            /* silently fail — viewport tab shows empty state */
        }
    }, [mapId, authHeader]);

    useEffect(() => {
        fetchData();
        fetchViewports();
    }, [fetchData, fetchViewports]);

    useEffect(() => {
        if (!analyticsData?.map?.isActive) return;
        const interval = setInterval(fetchData, 15000);
        return () => clearInterval(interval);
    }, [analyticsData?.map?.isActive, fetchData]);

    const handleRefresh = useCallback(async () => {
        setRefreshing(true);
        await Promise.all([fetchData(), fetchViewports()]);
        setRefreshing(false);
    }, [fetchData, fetchViewports]);

    if (loading) {
        return (
            <div className="ad-loading">
                <Loader2 size={28} className="ad-spin" />
                <p>Loading analytics…</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="ad-error">
                <h2>Failed to load analytics</h2>
                <p>{error}</p>
                <button onClick={() => navigate(-1)}>
                    <ArrowLeft size={14} /> Go back
                </button>
            </div>
        );
    }

    const { map } = analyticsData;
    const isActive = map?.isActive;

    return (
        <div className="ad-root">

            <header className="ad-header">
                <button className="ad-back-btn" onClick={() => navigate(-1)}>
                    <ArrowLeft size={15} /><span>Back</span>
                </button>
                <div className="ad-header-center">
                    <h1 className="ad-map-title">{map.title}</h1>
                    <span className={`ad-status-badge ${isActive ? 'active' : 'ended'}`}>
                        <span className="ad-status-dot" />
                        {isActive ? 'Live' : 'Ended'}
                    </span>
                    {isActive && (
                        <span className="ad-live-pulse">Auto-refreshing every 15s</span>
                    )}
                </div>
                <button
                    className="ad-refresh-btn"
                    onClick={handleRefresh}
                    disabled={refreshing}
                >
                    <RefreshCw size={14} className={refreshing ? 'ad-spin' : ''} />
                    <span>Refresh</span>
                </button>
            </header>

            <nav className="ad-tabs">
                {TABS.map(tab => (
                    <button
                        key={tab.id}
                        className={`ad-tab ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        {tab.label}
                    </button>
                ))}
            </nav>

            <main className="ad-main">

                <div style={{ display: activeTab === 'overview' ? 'block' : 'none' }}>
                    <OverviewTab
                        analyticsData={analyticsData}
                        onSeeAllMarkers={() => setActiveTab('markers')}
                    />
                </div>

                {/* Heatmap — NEVER unmounted, hidden via CSS only */}
                <div style={{ display: activeTab === 'heatmap' ? 'block' : 'none' }}>
                    <HeatmapTab
                        mapData={map}
                        pings={pings}
                        isActive={isActive}
                        isVisible={activeTab === 'heatmap'}
                    />
                </div>

                <div style={{ display: activeTab === 'markers' ? 'block' : 'none' }}>
                    <MarkersTab
                        analyticsData={analyticsData}
                        clicks={clicks}
                    />
                </div>

                <div style={{ display: activeTab === 'activity' ? 'block' : 'none' }}>
                    <ActivityTab
                        analyticsData={analyticsData}
                        clicks={clicks}
                        isActive={isActive}
                        authHeader={authHeader}
                    />
                </div>

                <div style={{ display: activeTab === 'sessions' ? 'block' : 'none' }}>
                    <SessionsTab analyticsData={analyticsData} />
                </div>

                <div style={{ display: activeTab === 'zoom' ? 'block' : 'none' }}>
                    <ZoomTab viewports={viewports} />
                </div>

            </main>
        </div>
    );
};

export default AnalyticsDashboard;